import * as React from 'react';
import { useSelector } from 'react-redux';
import { memoizedSelectRolesAndServices } from '../selectors';
import { hasPermissions } from '../constants';
import { PermissionEnum } from '../types';

export interface WithPermissionProps {
  hasPerms?: boolean;
  permission?: PermissionEnum;
}

interface PermissionProps {
  permission?: PermissionEnum;
}

/**
 * This component will take a permission either in the props of the WrappedComponent
 * on the options of withPermission and inject a `hasPerms` prop into the
 * WrappedComponent. This allows you to decide what you want to do inside of the component
 * depending on whether or not permissions have been granted.
 *
 * @param options { permission: PermissionEnum } permission to check against
 */
export const withPermission =
  (options?: PermissionProps) =>
  <P extends object>(
    Component: React.ComponentType<P & PermissionProps>,
  ): React.FunctionComponent<P & WithPermissionProps> => {
    return ({ permission: perm, ...props }) => {
      const permToUse = options ? options.permission : perm;
      if (permToUse == null) {
        console.warn(
          `${Component.name} No permission passed to HOC or in component props`,
        );
        return null;
      }

      const { roles, services } = useSelector(memoizedSelectRolesAndServices);
      const hasPerms = hasPermissions(roles, services, permToUse);
      return (
        <Component {...(props as P & PermissionProps)} hasPerms={hasPerms} />
      );
    };
  };
